import type { Chat } from "@ai-sdk/vue";
import type { UIMessage } from "ai";
import type { Ref } from "vue";

export interface QueuedChatFile {
    type: "file";
    mediaType: string;
    filename?: string;
    url: string;
    extractedText?: string;
}

export interface QueuedChatMessage {
    message: UIMessage;
    text: string;
    files: QueuedChatFile[];
    status: "saving" | "queued" | "processing" | "uncertain";
}

/**
 * Allows a Chat instance to be "handed off" from one component to another
 * without killing the active SSE stream. Used when navigating from the
 * full-page chat to a different page while opening the sidebar chat.
 */
let _pending: {
    chat: Chat<UIMessage>;
    chatId: string;
    implementationDetailsMessageId: Ref<string | undefined>;
    queuedMessage: Ref<QueuedChatMessage | null>;
} | null = null;

export function useChatHandoff() {
    function handoff(
        chat: Chat<UIMessage>,
        chatId: string,
        implementationDetailsMessageId: Ref<string | undefined>,
        queuedMessage: Ref<QueuedChatMessage | null>,
    ) {
        _pending = { chat, chatId, implementationDetailsMessageId, queuedMessage };
    }

    function claim(chatId: string): {
        chat: Chat<UIMessage>;
        implementationDetailsMessageId: Ref<string | undefined>;
        queuedMessage: Ref<QueuedChatMessage | null>;
    } | null {
        if (_pending && _pending.chatId === chatId) {
            const pending = _pending;
            _pending = null;
            return {
                chat: pending.chat,
                implementationDetailsMessageId: pending.implementationDetailsMessageId,
                queuedMessage: pending.queuedMessage,
            };
        }
        return null;
    }

    function isPending(): boolean {
        return _pending !== null;
    }

    return { handoff, claim, isPending };
}
